// /**
//  * Initializes the "click-outside" logic for the modal.
//  * We target the internal container to determine if the click hit the backdrop.
//  */
// const initModalClickOutside = (modal) => {
//   // Prevent adding multiple listeners to the same modal instance
//   if (modal.dataset.clickOutsideInitialized === 'true') return;

//   modal.addEventListener('click', (event) => {
//     const rect = modal.getBoundingClientRect();

//     // Check if the click coordinates are within the boundaries of the inner container
//     const isInDialog = (
//       event.clientX >= rect.left &&
//       event.clientX <= rect.right &&
//       event.clientY >= rect.top &&
//       event.clientY <= rect.bottom
//     );

//     // If the click is outside the container (on the backdrop), close the modal
//     if (!isInDialog) {
//       modal.close();
//     }
//   });

//   // Mark as initialized
//   modal.dataset.clickOutsideInitialized = 'true';
// };

// /**
//  * MutationObserver to detect when the configurator is injected into the DOM.
//  * This is necessary because the modal is added dynamically via JS after page load.
//  */
// const modalObserver = new MutationObserver((mutations) => {
//   mutations.forEach((mutation) => {
//     // Check for newly added nodes
//     mutation.addedNodes.forEach((node) => {
//       // Check if the added node is the dialog or contains the dialog
//       if (node.nodeType === 1) { // Node.ELEMENT_NODE
//         const dialogElement = node.tagName === 'DIALOG' ? node : node.querySelector('dialog');

//         if (dialogElement) {
//           console.log(dialogElement)
//           initModalClickOutside(dialogElement);
//         }
//       }
//     });
//   });
// });

// // Start observing the document body for injected configurator elements
// modalObserver.observe(document.body, {
//   childList: true,
//   subtree: true
// });

/**
 * Initialises scroll-based animations for lazy-loaded images,
 * including elements injected into the DOM after the initial page load.
 */
function onScrollAnimations() {
  const selector = 'img[loading="lazy"]';

  // Configuration for the IntersectionObserver (detects when elements enter viewport)
  const ioOptions = {
    rootMargin: "5%",
  };

  /**
   * IntersectionObserver Callback
   * Triggered when an observed image enters or leaves the "rootMargin" area.
   */
  const ioCallback = (entries, observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        // Apply the loaded state to trigger CSS animations
        entry.target.setAttribute("data-animation", "loaded");

        // Stop observing the element once it has been processed
        observer.unobserve(entry.target);
      }
    });
  };

  const intersectionObserver = new IntersectionObserver(ioCallback, ioOptions);

  /**
   * Helper function to find and observe matching elements within a specific node
   * @param {Node} container - The element or document to search within
   */
  const observeImages = (container) => {
    // Ensure the container itself matches or search for children
    const images = container.querySelectorAll
      ? container.querySelectorAll(selector)
      : [];
    images.forEach((img) => intersectionObserver.observe(img));
  };

  // 1. Initial execution for elements already present in the DOM
  observeImages(document);

  // 2. MutationObserver to handle client-side injected content
  const mutationCallback = (mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        // Only process Element nodes (nodeType 1)
        if (node.nodeType === 1) {
          // If the added node itself is a lazy image, observe it
          if (node.matches(selector)) {
            intersectionObserver.observe(node);
          }
          // Also check if the added node contains any lazy images as children
          observeImages(node);
        }
      });
    });
  };

  const mutationObserver = new MutationObserver(mutationCallback);

  // Watch the entire body for added child elements or nested tree changes
  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

// Initialize the script
onScrollAnimations();

/**
 * Web Component: Zoom
 * Version: 1.0
 *
 * Displays media in full screen mode.
 * Handles state only (CSS reacts to attributes).
 */
class Zoom extends HTMLElement {
  static tagName = "s-zoom";

  /**
   * Centralized selectors used by the component.
   * Keeps query logic in one place and avoids magic strings.
   */
  static selectors = {
    mediaItem: '[data-ref="media"]',
    closeButton: '[data-ref="zoom-close"]',
    zoomSlides: "[data-zoom-desktop-slides]",
    mobileSlides: "[data-zoom-mobile-slides]",
    productScope: "[data-zoom-product-scope]",
    slide: '[data-ref="slide"]',
    slideContainer: '[data-ref="container"]',
  };

  static mobileZoomMql = window.matchMedia("(max-width: 1149px)");

  static mobileSlidesHost(zoom) {
    return zoom
      .closest(Zoom.selectors.productScope)
      ?.querySelector(Zoom.selectors.mobileSlides);
  }

  // Component lifecycle
  // ----------------------------------------------------------------------

  connectedCallback() {
    // If DOM is already ready, run setup immediately
    if (document.readyState !== "loading") {
      this.setup();
      // Otherwise wait until DOM is ready
    } else {
      document.addEventListener("DOMContentLoaded", () => this.setup(), {
        once: true,
      });
    }
  }

  disconnectedCallback() {
    this.cleanup();
  }

  // Lifecycle hooks
  // ----------------------------------------------------------------------

  setup() {
    /**
     * AbortController allows us to unregister
     * all event listeners in one single operation.
     */
    this.eventAbortController = new AbortController();

    /**
     * Cache the button responsible for deactivating zoom.
     * This button is the ONLY way to exit zoom mode.
     */
    this.closeButtonElement = this.querySelector(Zoom.selectors.closeButton);

    /**
     * Ensure the component starts in a predictable state.
     * CSS will rely on these attributes.
     */
    if (!this.hasAttribute("state")) {
      this.setAttribute("state", "inactive");
    }

    if (
      this.closeButtonElement &&
      !this.closeButtonElement.hasAttribute("data-state")
    ) {
      this.closeButtonElement.setAttribute("data-state", "inactive");
    }

    /**
     * Click handler
     * - Zoom open: a click outside the image and the controls closes it.
     * - Otherwise: a click on a media element opens zoom.
     */
    this.addEventListener(
      "click",
      (event) => {
        if (this.isZoomActive) {
          if (!event.target.closest("img, video, iframe, model-viewer, button")) {
            this.deactivateZoom();
          }
          return;
        }

        const mediaElement = event.target.closest(Zoom.selectors.mediaItem);
        if (!mediaElement || !this.contains(mediaElement)) return;
        this.activateZoom(mediaElement);
      },
      { signal: this.eventAbortController.signal },
    );

    /**
     * Mobile carousel: open zoom on the desktop slide with the same media index.
     */
    const mobileSlidesHost = Zoom.mobileSlidesHost(this);
    if (mobileSlidesHost) {
      mobileSlidesHost.addEventListener(
        "click",
        (event) => {
          if (!Zoom.mobileZoomMql.matches) return;
          const mediaElement = event.target.closest(Zoom.selectors.mediaItem);
          if (!mediaElement || !mobileSlidesHost.contains(mediaElement))
            return;
          const slideLi = mediaElement.closest(Zoom.selectors.slide);
          const pos = slideLi?.dataset.mediaPosition;
          if (pos === undefined) return;
          const zoomSlide = this.querySelector(
            `${Zoom.selectors.slide}[data-media-position="${pos}"]`,
          );
          const zoomMedia = zoomSlide?.querySelector(Zoom.selectors.mediaItem);
          if (!zoomMedia) return;
          this.activateZoom(zoomMedia);
        },
        { signal: this.eventAbortController.signal },
      );
    }

    /**
     * Close button click handler
     * Deactivates zoom explicitly.
     */
    if (this.closeButtonElement) {
      this.closeButtonElement.addEventListener(
        "click",
        (event) => {
          event.preventDefault();
          this.deactivateZoom();
        },
        { signal: this.eventAbortController.signal },
      );
    }

    /**
     * Escape key handler
     * Close zoom when the user presses Escape.
     * We attach this to window so it works regardless of focus location.
     */
    window.addEventListener(
      "keydown",
      (event) => {
        // Only react when zoom is active.
        if (!this.isZoomActive) return;

        // Normalize Escape handling.
        if (event.key === "Escape" || event.key === "Esc") {
          event.preventDefault();
          this.deactivateZoom();
          return;
        }

        const slides = this.querySelector(Zoom.selectors.zoomSlides);
        if (!slides || typeof slides.goToSnapIndex !== "function") return;

        const idx = Number.isFinite(slides.activeIndex) ? slides.activeIndex : 0;

        if (event.key === "ArrowLeft") {
          event.preventDefault();
          slides.goToSnapIndex(idx - 1, "smooth");
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          slides.goToSnapIndex(idx + 1, "smooth");
        }
      },
      { signal: this.eventAbortController.signal },
    );
  }

  cleanup() {
    /**
     * Abort all event listeners registered with the controller.
     * This guarantees no memory leaks when the component is removed.
     */
    if (this.eventAbortController) {
      this.eventAbortController.abort();
    }

    /**
     * Make sure scroll is restored if the component
     * is removed while zoom is active.
     */
    this.unlockScroll();

    /**
     * Clear references to DOM nodes.
     * Not strictly required, but keeps intent explicit.
     */
    this.eventAbortController = null;
    this.closeButtonElement = null;
  }

  // State helpers
  // ----------------------------------------------------------------------

  /**
   * Returns true when zoom is currently active.
   */
  get isZoomActive() {
    return this.getAttribute("state") === "active";
  }

  // Scroll locking helpers
  // ----------------------------------------------------------------------

  lockScroll() {
    this.htmlOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
  }

  unlockScroll() {
    document.documentElement.style.overflow = this.htmlOverflow || "";
    this.htmlOverflow = null;
  }

  // Methods
  // ----------------------------------------------------------------------

  /**
   * Activates zoom mode
   * - Puts an inert copy of the gallery in its place, so the page behind still shows it
   * - Sets host attribute: state="active"
   * - Updates button state to data-state="active"
   * - Locks page scrolling
   * - Scrolls to the clicked media element
   *
   * CSS is responsible for all visual changes.
   */
  activateZoom(clickedMediaElement) {
    // Guard: do nothing if already active
    if (this.isZoomActive) return;

    this.setAttribute("state", "active");

    // Images already shown stay shown: no lazy fade-in when zoom opens or closes (nw-theme.css, @view Zoom)
    this.querySelectorAll("img").forEach((img) => img.setAttribute("data-zoom-seen", ""));

    // Keep the gallery visible on the page behind the zoom: a static, inert copy takes its place.
    // Added after the real gallery has left the page, so there are never two (no scroll jump).
    this.placeholder = this.cloneNode(true);
    this.placeholder.setAttribute("state", "inactive");
    this.placeholder.setAttribute("aria-hidden", "true");
    this.placeholder.inert = true;

    // Same "View More Images" state as the real gallery (a copied checkbox does not keep its ticked state)
    const moreToggle = this.querySelector(".product-media__more-toggle");
    const copyMoreToggle = this.placeholder.querySelector(".product-media__more-toggle");
    if (moreToggle && copyMoreToggle) copyMoreToggle.checked = moreToggle.checked;

    this.before(this.placeholder);

    if (this.closeButtonElement) {
      this.closeButtonElement.setAttribute("data-state", "active");
    }

    this.lockScroll();

    // Flush active layout before snaps/clones so scroll isn’t stuck at 0 (loop prepend = wrong slide flash).
    void this.offsetWidth;

    const slidesRoot = this.querySelector(Zoom.selectors.zoomSlides);
    slidesRoot?.setZoomLoopActive?.(true);

    /**
     * Scroll to the clicked media element
     *
     * Jump immediately after clones + updateInterface so we don’t flash the
     * prepended loop clone at scroll 0. One follow-up rAF recenters if the
     * browser adjusts flex/snap geometry after the first paint.
     *
     * Prefer jumpZoomLoopToSlide on <s-zoom-slides> when available;
     * otherwise scrollIntoView() aligns the snap target.
     */
    if (clickedMediaElement) {
      const slideEl = clickedMediaElement.closest(Zoom.selectors.slide);

      if (
        slideEl &&
        slidesRoot &&
        typeof slidesRoot.jumpZoomLoopToSlide === "function"
      ) {
        slidesRoot.jumpZoomLoopToSlide(slideEl);
        requestAnimationFrame(() => {
          slidesRoot.jumpZoomLoopToSlide(slideEl);
        });
      } else {
        requestAnimationFrame(() => {
          clickedMediaElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
          });
        });
      }
    }
  }

  /**
   * Deactivates zoom mode
   * - Sets host attribute: state="inactive"
   * - Updates button state to data-state="inactive"
   * - Restores page scrolling
   */
  deactivateZoom() {
    // Guard: do nothing if already inactive
    if (!this.isZoomActive) return;

    const slidesRoot = this.querySelector(Zoom.selectors.zoomSlides);
    let logicalExitIndex = 0;
    if (slidesRoot && typeof slidesRoot.getZoomExitLogicalIndex === "function") {
      logicalExitIndex = slidesRoot.getZoomExitLogicalIndex();
    }

    slidesRoot?.setZoomLoopActive?.(false);

    const mobileSlides = Zoom.mobileSlidesHost(this);
    if (
      mobileSlides &&
      typeof mobileSlides.goToSnapIndex === "function" &&
      Zoom.mobileZoomMql.matches
    ) {
      // Before hiding zoom: jump gallery to exit slide with no scroll animation
      // (avoids a visible strip move right after fullscreen closes).
      mobileSlides.goToSnapIndex(logicalExitIndex, "instant");
    }

    this.setAttribute("state", "inactive");

    // The real gallery is back in place, so the copy goes
    this.placeholder?.remove();
    this.placeholder = null;

    if (this.closeButtonElement) {
      this.closeButtonElement.setAttribute("data-state", "inactive");
    }

    this.unlockScroll();
  }
}

// Define and register the custom element
window.customElements.define(Zoom.tagName, Zoom);

/**
 * Web Component: Slides
 * Version: 1.0
 *
 * A horizontal slider built on native scroll and snap positions.
 */
class Slides extends HTMLElement {
  static tagName = "s-slides";

  static selectors = {
    container: '[data-ref="container"]',
    slide: '[data-ref="slide"]',
    navigation: '[data-ref="navigation"]',
    prev: '[data-ref="prev"]',
    next: '[data-ref="next"]',
    pagination: '[data-ref="pagination"]',
    scrollbar: '[data-ref="scrollbar"]',
    thumb: '[data-ref="thumb"]',
    counter: '[data-ref="counter"]',
  };

  // Component lifecycle
  // ----------------------------------------------------------------------

  connectedCallback() {
    // If DOM is already ready, run setup immediately
    if (document.readyState !== "loading") {
      this.setup();
      // Otherwise wait until DOM is ready
    } else {
      document.addEventListener("DOMContentLoaded", () => this.setup(), {
        once: true,
      });
    }
  }

  disconnectedCallback() {
    this.cleanup();
  }

  // Lifecycle hooks
  // ----------------------------------------------------------------------

  setup() {
    // Required elements
    this.container = this.querySelector(Slides.selectors.container);
    this.slides = Array.from(
      this.container?.querySelectorAll(Slides.selectors.slide) ?? [],
    );

    // Stop initialization if required elements are missing
    if (!this.hasSlides()) return;

    // Optional elements
    this.scrollbar = this.querySelector(Slides.selectors.scrollbar);
    this.thumb = this.scrollbar?.querySelector(Slides.selectors.thumb);
    this.navigation = this.querySelector(Slides.selectors.navigation);
    this.prevButton = this.navigation?.querySelector(Slides.selectors.prev);
    this.nextButton = this.navigation?.querySelector(Slides.selectors.next);
    this.pagination = this.querySelector(Slides.selectors.pagination);
    this.counter = this.querySelector(Slides.selectors.counter);

    // State
    this.activeIndex = 0;
    this.snaps = [];
    this.isScrolling = false;
    this.isSyncing = false;
    this.isDragging = false;
    this.dragPointerId = null;
    this.dragResistance = 0.95;
    this.paginationCount = 0;

    // Metrics
    this.totalScrollWidth = this.container.scrollWidth;
    this.containerWidth = this.container.clientWidth;
    this.maxScrollLeft = Math.max(
      0,
      this.totalScrollWidth - this.containerWidth,
    );

    // Abort controller for scoped listeners
    this.abortController = new AbortController();
    const abortSignal = this.abortController.signal;

    // Bind handlers
    this.handleScroll = this.handleScroll.bind(this);
    this.handleScrollbarClick = this.handleScrollbarClick.bind(this);
    this.handleThumbDown = this.handleThumbDown.bind(this);
    this.handleThumbMove = this.handleThumbMove.bind(this);
    this.handleThumbUp = this.handleThumbUp.bind(this);
    this.handlePaginationDotClick = this.handlePaginationClick.bind(this);
    this.handleNavigationPrevClick = () => this.handleNavigationClick(-1);
    this.handleNavigationNextClick = () => this.handleNavigationClick(1);
    this.handleTrackDown = this.handleTrackDown.bind(this);

    // Attach listeners
    this.container.addEventListener("scroll", this.handleScroll, {
      signal: abortSignal,
      passive: true,
    });
    this.scrollbar?.addEventListener("click", this.handleScrollbarClick, {
      signal: abortSignal,
    });
    this.prevButton?.addEventListener("click", this.handleNavigationPrevClick, {
      signal: abortSignal,
    });
    this.nextButton?.addEventListener("click", this.handleNavigationNextClick, {
      signal: abortSignal,
    });
    this.pagination?.addEventListener("click", this.handlePaginationDotClick, {
      signal: abortSignal,
    });
    this.thumb?.addEventListener("pointerdown", this.handleThumbDown, {
      signal: abortSignal,
    });
    this.thumb?.addEventListener("pointerup", this.handleThumbUp, {
      signal: abortSignal,
    });
    window.addEventListener("pointermove", this.handleThumbMove, {
      signal: abortSignal,
    });
    this.container.addEventListener("pointerdown", this.handleTrackDown, {
      signal: abortSignal,
    });
    this.container.addEventListener("dragstart", (event) => event.preventDefault(), {
      signal: abortSignal,
    });

    // Initial cold-path sync
    this.updateInterface({ reason: "setup" });

    // Start observers
    this.setupResizeObserver();
    this.setupMutationObserver();
  }

  cleanup() {
    // Abort listeners bound with signal
    this.abortController.abort();
    this.abortController = null;

    // Disconnect observers
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.mutationObserver?.disconnect();
    this.mutationObserver = null;

    // Reset flags and runtime state
    this.isScrolling = false;
    this.isSyncing = false;
    this.isDragging = false;
    this.activeIndex = 0;
    this.lastActiveIndex = -1;
    this.snaps = [];
    this.paginationCount = 0;

    // Reset pagination buttons
    this.dotButtons = [];
  }

  // Preconditions
  // ----------------------------------------------------------------------

  hasSlides() {
    if (!this.container) return false;
    if (!Array.isArray(this.slides) || this.slides.length === 0) return false;
    return true;
  }

  hasSnaps() {
    return Array.isArray(this.snaps) && this.snaps.length > 0;
  }

  hasScrollbar() {
    if (!this.scrollbar) return false;
    if (!this.thumb) return false;
    return true;
  }

  hasNavigation() {
    if (!this.navigation) return false;
    if (!this.prevButton || !this.nextButton) return false;
    return true;
  }

  hasPagination() {
    if (!this.pagination) return false;
    return true;
  }

  isReady() {
    return this.hasSlides() && this.hasSnaps();
  }

  slideCountForUi() {
    return this.snaps.length;
  }

  physicalToLogicalSlideIndex(physicalIndex) {
    return physicalIndex;
  }

  // Observers
  // ----------------------------------------------------------------------

  setupResizeObserver() {
    // Disconnect previous observer before re-creating
    this.resizeObserver?.disconnect();

    // Resize can fire many times rapidly, so we schedule a batched sync
    this.resizeObserver = new ResizeObserver(() => {
      this.scheduleUpdateInterface({ reason: "resize" });
    });

    // Observe container width changes (affects snap positions)
    this.resizeObserver.observe(this.container);
  }

  setupMutationObserver() {
    // Disconnect previous observer before re-creating
    this.mutationObserver?.disconnect();

    // Mutation can fire many times rapidly, so we schedule a batched sync
    this.mutationObserver = new MutationObserver(() => {
      this.scheduleUpdateInterface({ reason: "mutation" });
    });

    // Only observe direct children changes
    this.mutationObserver.observe(this.container, {
      childList: true,
      subtree: false,
    });
  }

  // Core methods
  // ----------------------------------------------------------------------

  syncActiveIndex() {
    this.activeIndex = this.getClosestSnapIndex(this.container.scrollLeft);
  }

  syncInterface() {
    if (!this.isReady()) return;

    // Sync UI based on current scroll position
    this.syncActiveIndex();
    this.syncNavigation();
    this.syncScrollbar();
    this.syncPagination();
    this.syncCounter();
  }

  updateSlides() {
    this.slides = Array.from(
      this.container?.querySelectorAll(Slides.selectors.slide) ?? [],
    );
  }

  updateContainerMetrics() {
    this.totalScrollWidth = this.container.scrollWidth;
    this.containerWidth = this.container.clientWidth;
    this.maxScrollLeft = Math.max(
      0,
      this.totalScrollWidth - this.containerWidth,
    );
  }

  scheduleUpdateInterface({ reason } = {}) {
    // Avoid multiple queued frames
    if (this.isSyncing) return;
    this.isSyncing = true;

    requestAnimationFrame(() => {
      this.isSyncing = false;
      this.updateInterface({ reason });
    });
  }

  updateInterface({ reason } = {}) {
    const shouldUpdate = {
      slides: true,
      metrics: true,
      snaps: true,
      pagination: true,
      counter: true,
    };

    if (reason === "resize") {
      // Skip DOM query on resize, slides likely did not change
      shouldUpdate.slides = false;
    }

    if (shouldUpdate.slides) {
      this.updateSlides();
    }

    // Exit if slides are missing
    if (!this.hasSlides()) {
      this.snaps = [];
      return;
    }

    if (shouldUpdate.metrics) {
      this.updateContainerMetrics();
      this.updateScrollbarMetrics();
    }

    if (shouldUpdate.snaps) {
      this.updateSnaps();
    }

    if (shouldUpdate.pagination) {
      this.buildPagination();
    }

    if (shouldUpdate.counter) {
      this.buildCounter();
    }

    // Sync UI derived from scroll position (hot path)
    this.syncInterface();
  }

  handleScroll() {
    // Avoid multiple queued frames
    if (this.isScrolling) return;
    this.isScrolling = true;

    requestAnimationFrame(() => {
      this.isScrolling = false;
      this.syncInterface();
      if (this.isLoopMode) this.scheduleZoomLoopCorrection();
    });
  }

  // Snap methods
  // ----------------------------------------------------------------------

  computeSnaps() {
    if (!this.hasSlides()) return [];

    const containerRect = this.container.getBoundingClientRect();
    const currentScrollLeft = this.container.scrollLeft;

    const positions = this.slides.map((slide) => {
      // Get computed styles for snapping data
      const slideStyles = getComputedStyle(slide);
      const snapAlignValue = slideStyles.scrollSnapAlign;
      const scrollMarginLeft = parseFloat(slideStyles.scrollMarginLeft) || 0;
      const scrollMarginRight = parseFloat(slideStyles.scrollMarginRight) || 0;
      const slideRect = slide.getBoundingClientRect();

      // Get start, center and end alignments as scroll coords
      const slideAlignStart =
        currentScrollLeft + (slideRect.left - containerRect.left);
      const slideAlignEnd =
        currentScrollLeft + (slideRect.right - containerRect.left);
      const slideAlignCenter = slideAlignStart + slideRect.width / 2;

      // Supports center alignment
      if (snapAlignValue.includes("center")) {
        return slideAlignCenter - this.containerWidth / 2;
      }

      // Supports start alignment
      if (snapAlignValue.includes("start")) {
        return slideAlignStart - scrollMarginLeft;
      }

      // Defaults to end alignment
      return slideAlignEnd - this.containerWidth + scrollMarginRight;
    });

    const clamped = positions.map((position) =>
      Math.max(0, Math.min(this.maxScrollLeft, position)),
    );

    if (this.isLoopMode) {
      return clamped;
    }

    return clamped
      // Sort ensures stable index mapping
      .sort((a, b) => a - b)
      // Remove near-duplicates caused by rounding and subpixels
      .filter(
        (position, index, array) =>
          index === 0 || Math.abs(position - array[index - 1]) > 1,
      );
  }

  updateSnaps() {
    const snaps = this.computeSnaps();
    this.snaps = Array.isArray(snaps) ? snaps : [];
  }

  clampSnapIndex(index) {
    if (!this.hasSnaps()) return 0;
    return Math.max(0, Math.min(this.snaps.length - 1, Math.round(index)));
  }

  getClosestSnapIndex(currentScrollLeft) {
    if (!this.hasSnaps()) return 0;

    // Start with the first snap
    let closestIndex = 0;
    let closestDistance = Math.abs(currentScrollLeft - this.snaps[0]);

    // Check remaining snaps
    for (let index = 1; index < this.snaps.length; index++) {
      const distance = Math.abs(currentScrollLeft - this.snaps[index]);

      // If this snap is closer, update best
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    }

    return closestIndex;
  }

  goToSnapIndex(index, behavior = "smooth") {
    if (!this.hasSnaps()) return;

    // Validate index
    index = Number(index);
    if (!Number.isFinite(index)) return;

    // Clamp index into valid range
    const clampedIndex = this.clampSnapIndex(index);

    // Translate index to scrollLeft value
    const target = this.snaps[clampedIndex];

    // Perform scroll
    this.scrollToTarget(target, behavior);
  }

  // Mouse drag on the slides themselves (touch already swipes natively)
  // ----------------------------------------------------------------------

  handleTrackDown(event) {
    if (event.pointerType !== "mouse" || event.button !== 0) return;
    if (this.maxScrollLeft <= 0) return;
    // Buttons in a slide (e.g. Customer Inspiration's images) can be dragged too; a drag is never a click
    if (event.target.closest("input, video, model-viewer, iframe")) return;

    const startX = event.clientX;
    const startScrollLeft = this.container.scrollLeft;
    let moved = false;

    // Snapping off while dragging, so the row follows the mouse
    this.container.style.scrollSnapType = "none";
    this.container.style.scrollBehavior = "auto";

    const onMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      if (Math.abs(delta) > 4) moved = true;
      this.container.scrollLeft = startScrollLeft - delta;
    };

    const onUp = async () => {
      window.removeEventListener("pointermove", onMove);

      // A drag is not a click: no link or zoom opens on release
      if (moved) {
        const blockClick = (clickEvent) => {
          clickEvent.preventDefault();
          clickEvent.stopPropagation();
        };
        this.container.addEventListener("click", blockClick, { capture: true, once: true });
        setTimeout(() => this.container.removeEventListener("click", blockClick, { capture: true }), 0);
      }

      // Ease to the closest slide, then hand snapping back to CSS
      const index = this.getClosestSnapIndex(this.container.scrollLeft);
      await this.scrollToTarget(this.snaps[index], "custom");
      this.container.style.removeProperty("scroll-snap-type");
      this.container.style.removeProperty("scroll-behavior");
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp, { once: true });
  }

  // Scrollbar methods
  // ----------------------------------------------------------------------

  syncScrollbar() {
    if (!this.hasScrollbar()) return;

    // Map scroll position to thumb position
    const ratio = this.container.scrollLeft / this.maxScrollLeft;
    const x = Math.round(ratio * this.maxThumbTravel);

    // Clamp into valid range
    const clampedX = Math.max(0, Math.min(this.maxThumbTravel, x));

    // Update only the transform for best performance
    this.thumb.style.transform = `translateX(${clampedX}px)`;
  }

  updateScrollbarMetrics() {
    if (!this.hasScrollbar()) return;

    // Store scrollbar width
    this.scrollbarWidth = this.scrollbar.clientWidth;

    // Compute thumb width
    const thumbWidth =
      (this.containerWidth / this.totalScrollWidth) * this.scrollbarWidth;

    // Minimum width for usability
    const minThumbWidth = 44;

    // Clamp thumb width into valid range
    this.thumbWidth = Math.max(
      minThumbWidth,
      Math.min(this.scrollbarWidth, thumbWidth),
    );

    // Add the thumb width inline
    this.thumb.style.width = `${this.thumbWidth}px`;

    // Compute maximum thumb travel
    this.maxThumbTravel = Math.max(0, this.scrollbarWidth - this.thumbWidth);
  }

  handleScrollbarClick(event) {
    if (!this.hasScrollbar()) return;

    // Avoid thumb drags on track clicks
    if (event.target === this.thumb) return;

    // Read layout once (cold path)
    const rect = this.scrollbar.getBoundingClientRect();

    // Click position inside the track
    const clickX = event.clientX - rect.left;

    // Center the thumb on click
    const targetThumbX = clickX - this.thumbWidth / 2;

    // Map thumb position to ratio
    const ratio = Math.max(0, Math.min(1, targetThumbX / this.maxThumbTravel));

    // Map ratio to scrollLeft
    const targetScrollLeft = ratio * this.maxScrollLeft;

    this.scrollToTarget(targetScrollLeft, "smooth");
  }

  handleThumbDown(event) {
    if (!this.hasScrollbar()) return;

    // Prevents text selection while dragging
    event.preventDefault();

    // Marks drag as active
    this.isDragging = true;

    // Expose drag state to CSS
    this.setAttribute("dragging", "true");

    // Store the pointer id for multi-touch safety
    this.dragPointerId = event.pointerId;

    // Capture the pointer for reliable dragging
    this.thumb.setPointerCapture(event.pointerId);

    // Store starting geometry references
    this.dragStartClientX = event.clientX;
    this.dragStartScrollLeft = this.container.scrollLeft;

    // Disable snap and smooth scrolling during drag
    this.container.style.scrollSnapType = "none";
    this.container.style.scrollBehavior = "auto";
  }

  handleThumbMove(event) {
    if (!this.isDragging) return;

    // Ignore unrelated pointers
    if (this.dragPointerId !== event.pointerId) return;

    // Pointer delta from drag start
    const pointerDeltaX = event.clientX - this.dragStartClientX;

    // Convert thumb delta to scroll delta
    const ratio = (pointerDeltaX / this.maxThumbTravel) * this.dragResistance;
    const scrollDeltaX = ratio * this.maxScrollLeft;

    // Apply target scrollLeft
    const target = this.dragStartScrollLeft + scrollDeltaX;

    // Clamp into valid scroll range
    const clampedTarget = Math.max(0, Math.min(this.maxScrollLeft, target));

    // Write scrollLeft directly for best responsiveness
    this.container.scrollLeft = clampedTarget;
  }

  async handleThumbUp(event) {
    if (!this.isDragging) return;

    // Ignore unrelated pointers
    if (this.dragPointerId !== event.pointerId) return;

    // End the drag state
    this.isDragging = false;

    // Clear the pointer id
    this.dragPointerId = null;

    // Remove the CSS drag hook
    this.removeAttribute("dragging");

    // If we have snaps, settle to the nearest snap before restoring snap behavior
    if (this.hasSnaps()) {
      const index = this.getClosestSnapIndex(this.container.scrollLeft);
      const target = this.snaps[index];
      await this.scrollToTarget(target, "custom");
    }

    // Restore original scroll related styles
    this.container.style.scrollSnapType = "";
    this.container.style.scrollBehavior = "";
  }

  // Navigation methods
  // ----------------------------------------------------------------------

  syncNavigation() {
    if (!this.hasNavigation()) return;

    if (this.isLoopMode) {
      if (this.prevButton) this.prevButton.disabled = false;
      if (this.nextButton) this.nextButton.disabled = false;
      return;
    }

    // Tolerance to reduce flicker near boundaries
    const epsilon = 1;

    // Disable prev at the start
    if (this.prevButton) {
      this.prevButton.disabled = this.container.scrollLeft <= epsilon;
    }

    // Disable next at the end
    if (this.nextButton) {
      this.nextButton.disabled =
        this.container.scrollLeft >= this.maxScrollLeft - epsilon;
    }
  }

  handleNavigationClick(direction) {
    if (!this.isReady()) return;

    // Ensure activeIndex matches current scroll position
    this.syncActiveIndex();

    // Move one step
    this.goToSnapIndex(this.activeIndex + direction);
  }

  // Counter methods
  // ----------------------------------------------------------------------

  buildCounter() {
    if (!this.hasSnaps()) return;
    if (!this.counter) return;

    // Needs a snap-derived total (slideCountForUi respects loop real count)
    const total = this.slideCountForUi();

    // Store total so sync can be O(1) and we can detect changes
    this.counterTotal = total;

    // If there are no snaps, clear counter
    if (total === 0) {
      this.counter.textContent = "";
      // Also reset last rendered state
      this.lastCounterIndex = -1;
      return;
    }

    // Ensure initial render is forced
    this.lastCounterIndex = -1;

    // Render initial state immediately
    this.syncCounter();
  }

  syncCounter() {
    if (!this.counter) return;

    // Needs snaps and an active index
    if (!this.hasSnaps()) {
      this.counter.textContent = "";
      this.lastCounterIndex = -1;
      return;
    }

    const total = this.counterTotal ?? this.snaps.length;

    // Guard against weird states
    if (!Number.isFinite(total) || total <= 0) {
      this.counter.textContent = "";
      this.lastCounterIndex = -1;
      return;
    }

    // logical index (clone-aware) → counter is 1-based
    const logical = this.physicalToLogicalSlideIndex(this.activeIndex);
    const current = Math.max(1, Math.min(total, logical + 1));

    // Avoid DOM writes when unchanged
    if (this.lastCounterIndex === current && this.lastCounterTotal === total)
      return;

    this.counter.textContent = `${current} / ${total}`;

    // Cache what we rendered
    this.lastCounterIndex = current;
    this.lastCounterTotal = total;

    // Remove from UI if only one slide
    if (total < 2) {
      this.counter.style.opacity = "0";
    } else {
      this.counter.style.opacity = "1";
    }
  }

  // Pagination methods
  // ----------------------------------------------------------------------

  buildPagination() {
    if (!this.hasPagination()) return;

    // Decide whether we need a rebuild
    const snapCount = this.slideCountForUi();
    if (snapCount === this.paginationCount) return;

    // Store current count to avoid rebuilding on every sync
    this.paginationCount = snapCount;

    // Reset active cache so the next hot sync updates correct dot
    this.lastActiveIndex = -1;

    // Clear previous dots
    this.pagination.textContent = "";
    this.dotButtons = [];

    // Exit when there are no snaps
    if (snapCount === 0) return;

    // Create all dots using a fragment for minimal DOM work
    const fragment = document.createDocumentFragment();

    for (let index = 0; index < snapCount; index += 1) {
      const dotButton = document.createElement("button");

      // Prevent form submission
      dotButton.type = "button";

      // Styling hook
      dotButton.classList.add("slides__pagination-dot");

      // Target snap index (offset when loop clones prepend a column)
      dotButton.dataset.index = String(
        this.isLoopMode ? index + 1 : index,
      );

      // Accessible label
      dotButton.setAttribute("aria-label", `Go to snap ${index + 1}`);

      fragment.appendChild(dotButton);
      this.dotButtons.push(dotButton);
    }

    // Commit to DOM once
    this.pagination.appendChild(fragment);
  }

  syncPagination() {
    if (!this.hasPagination()) return;

    const nextIndex = this.physicalToLogicalSlideIndex(this.activeIndex);
    if (nextIndex === this.lastActiveIndex) return;

    const prevIndex = this.lastActiveIndex;
    const prevButton = this.dotButtons[prevIndex] ?? null;
    const nextButton = this.dotButtons[nextIndex] ?? null;

    // Clear previous active state
    if (prevButton) {
      prevButton.removeAttribute("aria-current");
      prevButton.removeAttribute("data-active");
    }

    // Set next active state
    if (nextButton) {
      nextButton.setAttribute("aria-current", "true");
      nextButton.setAttribute("data-active", "true");
    }

    this.lastActiveIndex = nextIndex;
  }

  handlePaginationClick(event) {
    if (!this.hasPagination()) return;

    // Find the closest button that belongs to this pagination
    const button = event.target?.closest?.("button[data-index]");
    if (!button) return;
    if (!this.pagination.contains(button)) return;

    const index = Number(button.dataset.index);
    if (!Number.isFinite(index)) return;

    this.goToSnapIndex(index);
  }

  // Helpers
  // ----------------------------------------------------------------------

  scrollToTarget(target, behavior = "smooth") {
    if (!this.isReady()) return;

    // Clamp into valid scroll range
    const clampedTarget = Math.max(0, Math.min(this.maxScrollLeft, target));

    // Custom animation that returns a Promise
    if (behavior === "custom") {
      return this.animateToTarget(clampedTarget);
    }

    if (behavior === "instant") {
      const c = this.container;
      c.style.scrollSnapType = "none";
      c.style.scrollBehavior = "auto";
      c.scrollLeft = clampedTarget;
      this.syncInterface();
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          c.style.removeProperty("scroll-snap-type");
          c.style.removeProperty("scroll-behavior");
        }),
      );
      return;
    }

    this.container.scrollTo({
      left: clampedTarget,
      behavior: behavior === "auto" ? "auto" : "smooth",
    });
  }

  animateToTarget(target) {
    return new Promise((resolve) => {
      // Handles the already-close case
      if (Math.abs(this.container.scrollLeft - target) < 1) {
        // Snaps to the final value
        this.container.scrollLeft = target;

        // Syncs the interface after snapping
        this.syncInterface();

        // Resolves the promise
        resolve();
        return;
      }

      // Set a short, native-like duration
      const duration = 300;

      // Get the starting position
      const startScrollLeft = this.container.scrollLeft;

      // Compute the distance to travel
      const deltaScrollLeft = target - startScrollLeft;

      // Store the start timestamp
      const startTime = performance.now();

      // Define an ease-out curve
      const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

      const step = (now) => {
        // Compute elapsed time
        const elapsed = now - startTime;

        // Normalizes progress to 0..1
        const progress = Math.min(1, elapsed / duration);

        // Add easing
        const easedProgress = easeOutCubic(progress);

        // Writes the animated scrollLeft
        this.container.scrollLeft =
          startScrollLeft + deltaScrollLeft * easedProgress;

        // Sync the interface during animation
        this.syncInterface();

        // Continues until progress reaches 1
        if (progress < 1) {
          requestAnimationFrame(step);
          return;
        }

        // Snaps to the exact final value
        this.container.scrollLeft = target;

        // Sync the interface at the end
        this.syncInterface();

        // Resolves the promise
        resolve();
      };

      // Starts the animation loop
      requestAnimationFrame(step);
    });
  }
}

/**
 * Web Component: ZoomSlides
 *
 * Extends Slides for the desktop <s-zoom> strip (optional loop clones).
 */
class ZoomSlides extends Slides {
  static tagName = "s-zoom-slides";

  slideCountForUi() {
    return this.isLoopMode ? this.loopRealCount : super.slideCountForUi();
  }

  _zoomOriginalSlides() {
    return this.container.querySelectorAll(
      `${Slides.selectors.slide}:not([data-loop-clone])`,
    );
  }

  setup() {
    this.isLoopMode = false;
    this.loopRealCount = 0;
    this.isLoopJumping = false;
    this.loopScrollEndTimer = null;
    super.setup();
    if (this.container && this.abortController) {
      this.container.addEventListener(
        "scrollend",
        () => this.finishZoomLoopCorrection(),
        { signal: this.abortController.signal, passive: true },
      );
    }
  }

  cleanup() {
    clearTimeout(this.loopScrollEndTimer);
    this.loopScrollEndTimer = null;
    this.teardownZoomLoopClones();
    super.cleanup();
  }

  setZoomLoopActive(active) {
    if (!this.container) return;
    if (active) this.installZoomLoopClones();
    else this.teardownZoomLoopClones();
    this.slides = Array.from(
      this.container.querySelectorAll(Slides.selectors.slide),
    );
    this.paginationCount = 0;
    this.lastActiveIndex = -1;
    this.updateInterface({ reason: "mutation" });
  }

  jumpZoomLoopToSlide(slideEl) {
    if (!this.container || !slideEl || !this.hasSnaps()) return;
    let i;
    if (this.isLoopMode) {
      const li = [...this._zoomOriginalSlides()].indexOf(slideEl);
      if (li < 0) return;
      i = li + 1;
    } else {
      i = this.slides.indexOf(slideEl);
    }
    if (i < 0 || !this.snaps[i]) return;
    this.scrollToTarget(this.snaps[i], "instant");
    this.syncInterface();
  }

  installZoomLoopClones() {
    if (!this.container || this.isLoopMode) return;
    const originals = [...this._zoomOriginalSlides()];
    this.loopRealCount = originals.length;
    if (originals.length < 2) return;
    this.isLoopMode = true;
    const first = originals[0];
    const last = originals[originals.length - 1];
    for (const [node, side] of [
      [last.cloneNode(true), "prepend"],
      [first.cloneNode(true), "append"],
    ]) {
      node.setAttribute("data-loop-clone", side);
      node.setAttribute("aria-hidden", "true");
      node.querySelectorAll("[id]").forEach((el) => el.removeAttribute("id"));
      node.querySelectorAll("img").forEach((img) => {
        img.loading = "eager";
        img.decode?.().catch(() => {});
      });
      if (side === "prepend")
        this.container.insertBefore(node, this.container.firstElementChild);
      else this.container.appendChild(node);
    }
    for (const root of [first, last]) {
      root.querySelectorAll("img").forEach((img) => {
        img.loading = "eager";
        img.decode?.().catch(() => {});
      });
    }
  }

  teardownZoomLoopClones() {
    if (!this.container) return;
    this.container
      .querySelectorAll("[data-loop-clone]")
      .forEach((n) => n.remove());
    this.isLoopMode = false;
    this.loopRealCount = this.container.querySelectorAll(
      Slides.selectors.slide,
    ).length;
  }

  physicalToLogicalSlideIndex(physicalIndex) {
    const n = this.loopRealCount;
    if (physicalIndex <= 0) return n - 1;
    if (physicalIndex >= n + 1) return 0;
    return physicalIndex - 1;
  }

  getZoomExitLogicalIndex() {
    if (!this.container) return 0;
    this.syncActiveIndex();
    return this.isLoopMode && this.loopRealCount > 1
      ? this.physicalToLogicalSlideIndex(this.activeIndex)
      : this.clampSnapIndex(this.activeIndex);
  }

  _nudgeOffCloneColumns(requireSettled) {
    if (!this.isLoopMode || this.isLoopJumping || !this.hasSnaps()) return;
    this.syncActiveIndex();
    const p = this.activeIndex;
    const n = this.loopRealCount;
    const target =
      p === 0 ? n : p === n + 1 ? 1 : null;
    if (target == null) return;
    if (requireSettled) {
      const at = this.snaps[p];
      if (Math.abs(this.container.scrollLeft - at) > 4) return;
    }
    this.isLoopJumping = true;
    this.scrollToTarget(this.snaps[target], "instant");
    requestAnimationFrame(() => {
      this.isLoopJumping = false;
    });
  }

  scheduleZoomLoopCorrection() {
    if (!this.isLoopMode) return;
    this._nudgeOffCloneColumns(true);
    clearTimeout(this.loopScrollEndTimer);
    this.loopScrollEndTimer = setTimeout(() => {
      this.loopScrollEndTimer = null;
      this._nudgeOffCloneColumns(false);
    }, 60);
  }

  finishZoomLoopCorrection() {
    if (!this.isLoopMode) return;
    clearTimeout(this.loopScrollEndTimer);
    this.loopScrollEndTimer = null;
    this._nudgeOffCloneColumns(false);
  }
}

// Define and register the custom elements
window.customElements.define(Slides.tagName, Slides);
window.customElements.define(ZoomSlides.tagName, ZoomSlides);

/**
 * Web Component: Reveal
 * Version: 1.0
 */
// class Reveal extends HTMLElement {
//   static tagName = 's-reveal';

//   static selectors = {
//     toggle: 'button',
//   };

//   // Component lifecycle
//   // ----------------------------------------------------------------------

//   connectedCallback() {
//     // If DOM is already ready, run setup immediately
//     if (document.readyState !== 'loading') {
//       this.setup();
//     // Otherwise wait until DOM is ready
//     } else {
//       document.addEventListener('DOMContentLoaded', () => this.setup(), {
//         once: true
//       });
//     }
//   }

//   disconnectedCallback() {
//     this.cleanup();
//   }

//   // Lifecycle hooks
//   // ----------------------------------------------------------------------

//   setup() {
//     // Required elements
//     this.toggle = this.querySelector(Reveal.selectors.toggle);
//     this.target = document.getElementById(this.getAttribute('target-id'));

//     // Stop initialization if required elements are missing
//     // if (!this.hasToggle() || !this.hasTarget()) return;
//     if (!this.toggle || !this.target) return;

//     // State
//     this.isTargetVisible =
//       (this.hasAttribute('target-visible') && this.getAttribute('target-visible') !== 'false') || false;

//     // Add button type to avoid submit action
//     if (this.toggle.getAttribute('type') !== 'button') {
//       this.toggle.setAttribute('type', 'button');
//     }

//     // Add aria attributes
//     this.toggle.setAttribute('aria-expanded', this.isTargetVisible);
//     this.toggle.setAttribute('aria-controls', this.target.id);

//     // Check target initial visible state
//     if (!this.isTargetVisible) {
//       this.target.setAttribute('data-expanded', 'false');
//     } else {
//       this.target.setAttribute('data-expanded', 'true');
//     }

//     // Abort controller for scoped listeners
//     this.abortController = new AbortController();
//     const abortSignal = this.abortController.signal;

//     // Bind handler
//     this.handleToggleClick = this.handleToggleClick.bind(this);

//     // Attach listener
//     this.toggle.addEventListener('click', this.handleToggleClick, { signal: abortSignal });
//   }

//   cleanup() {
//     // Abort all listeners registered with this.abortController
//     if (this.abortController) {
//       this.abortController.abort();
//       this.abortController = null;
//     }
//   }

//   // Preconditions
//   // ----------------------------------------------------------------------

//   // hasToggle() {
//   //   if (!this.toggle) {
//   //     console.error(`${this.localName}: missing required <button> element.`);
//   //     return false;
//   //   }
//   //   return true;
//   // }

//   // hasTarget() {
//   //   if (!this.target) {
//   //     console.error(`${this.localName}: missing required target element.`);
//   //     return false;
//   //   }
//   //   return true;
//   // }

//   // Reveal methods
//   // ----------------------------------------------------------------------

//   // Reads a CSS custom property (--reveal-transition-duration)
//   // and returns the duration in milliseconds.
//   getTransitionDuration(element) {
//     // Safety guard: if no element is provided, return 0ms.
//     if (!element) return 0;

//     // Read the CSS custom property from computed styles.
//     const value = getComputedStyle(element)
//       .getPropertyValue('--reveal-transition-duration')
//       .trim();

//     // If the variable is not set or empty, fallback to 0ms.
//     if (!value) return 0;

//     // If value is in milliseconds
//     if (value.endsWith('ms')) return parseFloat(value) || 0;

//     // If value is in seconds, convert to milliseconds
//     if (value.endsWith('s')) return (parseFloat(value) || 0) * 1000;

//     // Fallback: try to parse as a number (assume milliseconds)
//     const number = parseFloat(value);
//     return Number.isFinite(number) ? number : 0;
//   }

//   // Checks if the element is currently in a transition lock state
//   isLockedTransition(element) {
//     return element?.getAttribute('data-transitioning') === 'true';
//   }

//   // Marks the element as transitioning to prevent overlapping animations
//   lockTransition(element) {
//     element.setAttribute('data-transitioning', 'true');
//   }

//   // Removes the transition lock flag
//   unlockTransition(element) {
//     element.removeAttribute('data-transitioning');
//   }

//   // Expands the element with animated height and opacity
//   show(element) {
//     // Safety guard
//     if (!element) return;

//     // If a transition is already running, do nothing
//     if (this.isLockedTransition(element)) return;

//     // Lock transitions so we can't interrupt this animation
//     this.lockTransition(element);

//     // Mark the element as expanded so CSS can make it visible
//     element.setAttribute('data-expanded', 'true');

//     // Add an opening state which lets CSS start opacity at 0 before transitioning to 1
//     element.setAttribute('data-opening', 'true');

//     // Force a starting height of 0 so we can animate height to content height
//     element.style.height = '0px';

//     // Wait for the next animation frame so the browser registers
//     // the initial styles before we change them
//     requestAnimationFrame(() => {
//       // Remove the opening flag so opacity can transition to 1
//       element.removeAttribute('data-opening');

//       // Set the target height, triggering the height transition
//       element.style.height = `${element.scrollHeight}px`;
//     });

//     // Read how long the transition should last
//     const duration = this.getTransitionDuration(element);

//     // After the transition
//     window.setTimeout(() => {
//       // Remove the inline height so layout can become flexible again
//       element.style.height = '';

//       // Unlock transitions so future toggles are allowed
//       this.unlockTransition(element);
//     }, duration);
//   }

//   // Collapses the element with animated height and opacity
//   hide(element) {
//     // Safety guard
//     if (!element) return;

//     // Prevent starting a new animation if one is already running
//     if (this.isLockedTransition(element)) return;

//     // Lock transitions so we can't interrupt this animation
//     this.lockTransition(element);

//     // Ensure we are not in an opening state anymore
//     element.removeAttribute('data-opening');

//     // Freeze the current height so we can animate from
//     // this exact value down to 0
//     element.style.height = `${element.scrollHeight}px`;

//     // Add a closing state so CSS can fade opacity to 0
//     element.setAttribute('data-closing', 'true');

//     // Wait one frame so the frozen height is applied,
//     // then trigger the collapse
//     requestAnimationFrame(() => {
//       // Setting height to 0 triggers the height transition
//       element.style.height = '0px';
//     });

//     // Read how long the transition should last
//     const duration = this.getTransitionDuration(element);

//     // After the transition
//     window.setTimeout(() => {
//       // Remove the closing state
//       element.removeAttribute('data-closing');

//       // Mark the element as no longer expanded
//       element.setAttribute('data-expanded', 'false');

//       // Remove inline height so CSS can control layout again
//       element.style.height = '';

//       // Unlock transitions
//       this.unlockTransition(element);
//     }, duration);
//   }

//   // Toggles between show() and hide() depending on current state
//   reveal(element) {
//     // Read current expanded state from attribute
//     const isExpanded =
//       element?.getAttribute('data-expanded') === 'true';

//     // Collapse if expanded, otherwise expand
//     if (isExpanded) this.hide(element);
//     else this.show(element);
//   }

//   handleToggleClick() {
//     // Read expanded state from aria-expanded
//     const isExpanded = this.toggle.getAttribute('aria-expanded') === 'true';

//     // If we are about to open, close other items in the same group first
//     if (!isExpanded) {
//       this.closeOtherGroupItems();
//     }

//     // Flip aria state first
//     this.toggle.setAttribute('aria-expanded', !isExpanded);

//     // Toggle reveal animation on the target
//     this.reveal(this.target);
//   }

//   // Grouping methods
//   // ----------------------------------------------------------------------

//   // Returns the group name for this component, if any
//   getGroupName() {
//     const name = this.getAttribute('name');
//     return name && name.trim() ? name.trim() : null;
//   }

//   // Returns all other <wc-reveal> instances in the same group
//   getGroupItems() {
//     const groupName = this.getGroupName();
//     if (!groupName) return [];

//     // Find all reveals with the same name in the document
//     const allReveals = Array.from(document.querySelectorAll(`${Reveal.tagName}[name="${CSS.escape(groupName)}"]`));

//     // Exclude this instance
//     return allReveals.filter((element) => element !== this);
//   }

//   // Closes all other group items so only one stays open
//   closeOtherGroupItems() {
//     const items = this.getGroupItems();
//     if (!items.length) return;

//     items.forEach((item) => {
//       // Find the other instance's target the same way it does in setup()
//       const itemTargetId = item.getAttribute('target-id');
//       const itemTarget = itemTargetId ? document.getElementById(itemTargetId) : null;

//       // If target is missing, skip
//       if (!itemTarget) return;

//       // If target is already closed, skip
//       const isItemExpanded = itemTarget.getAttribute('data-expanded') === 'true';
//       if (!isItemExpanded) return;

//       // If item is currently transitioning, skip to avoid fighting its animation
//       if (itemTarget.getAttribute('data-transitioning') === 'true') return;

//       // Update the other button's aria-expanded (if it exists)
//       const itemToggle = item.querySelector(Reveal.selectors.toggle);
//       if (itemToggle) itemToggle.setAttribute('aria-expanded', 'false');

//       // Close the other target using the same animation logic
//       // We call the instance method so it uses its own helpers if needed.
//       item.hide(itemTarget);
//     });
//   }
// }

// // Define and register the custom element
// window.customElements.define(Reveal.tagName, Reveal);

class Reveal extends HTMLElement {
  static tagName = "s-reveal";

  static selectors = {
    toggle: "button",
  };

  // Component lifecycle
  // ----------------------------------------------------------------------

  connectedCallback() {
    // If DOM is already ready, run setup immediately
    if (document.readyState !== "loading") {
      this.setup();
      // Otherwise wait until DOM is ready
    } else {
      document.addEventListener("DOMContentLoaded", () => this.setup(), {
        once: true,
      });
    }
  }

  disconnectedCallback() {
    this.cleanup();
  }

  // Lifecycle hooks
  // ----------------------------------------------------------------------

  setup() {
    // Required elements
    this.toggle = this.querySelector(Reveal.selectors.toggle);
    this.target = document.getElementById(this.getAttribute("target-id"));

    // Stop initialization if required elements are missing
    // if (!this.hasToggle() || !this.hasTarget()) return;
    if (!this.toggle || !this.target) return;

    // State
    this.isTargetVisible =
      (this.hasAttribute("target-visible") &&
        this.getAttribute("target-visible") !== "false") ||
      false;

    // Add button type to avoid submit action
    if (this.toggle.getAttribute("type") !== "button") {
      this.toggle.setAttribute("type", "button");
    }

    // Add aria attributes
    this.toggle.setAttribute("aria-expanded", this.isTargetVisible);
    this.toggle.setAttribute("aria-controls", this.target.id);

    // Check target initial visible state
    if (!this.isTargetVisible) {
      this.target.setAttribute("data-expanded", "false");
    } else {
      this.target.setAttribute("data-expanded", "true");
    }

    // Abort controller for scoped listeners
    this.abortController = new AbortController();
    const abortSignal = this.abortController.signal;

    // Bind handler
    this.handleToggleClick = this.handleToggleClick.bind(this);

    // Attach listener
    this.toggle.addEventListener("click", this.handleToggleClick, {
      signal: abortSignal,
    });
  }

  cleanup() {
    // Abort all listeners registered with this.abortController
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  // Preconditions
  // ----------------------------------------------------------------------

  // hasToggle() {
  //   if (!this.toggle) {
  //     console.error(`${this.localName}: missing required <button> element.`);
  //     return false;
  //   }
  //   return true;
  // }

  // hasTarget() {
  //   if (!this.target) {
  //     console.error(`${this.localName}: missing required target element.`);
  //     return false;
  //   }
  //   return true;
  // }

  // Reveal methods
  // ----------------------------------------------------------------------

  // Reads a CSS custom property (--reveal-transition-duration)
  // and returns the duration in milliseconds.
  getTransitionDuration(element) {
    // Safety guard: if no element is provided, return 0ms.
    if (!element) return 0;

    // Read the CSS custom property from computed styles.
    const value = getComputedStyle(element)
      .getPropertyValue("--reveal-transition-duration")
      .trim();

    // If the variable is not set or empty, fallback to 0ms.
    if (!value) return 0;

    // If value is in milliseconds
    if (value.endsWith("ms")) return parseFloat(value) || 0;

    // If value is in seconds, convert to milliseconds
    if (value.endsWith("s")) return (parseFloat(value) || 0) * 1000;

    // Fallback: try to parse as a number (assume milliseconds)
    const number = parseFloat(value);
    return Number.isFinite(number) ? number : 0;
  }

  // Checks if the element is currently in a transition lock state
  isLockedTransition(element) {
    return element?.getAttribute("data-transitioning") === "true";
  }

  // Marks the element as transitioning to prevent overlapping animations
  lockTransition(element) {
    element.setAttribute("data-transitioning", "true");
  }

  // Removes the transition lock flag
  unlockTransition(element) {
    element.removeAttribute("data-transitioning");
  }

  // Expands the element with animated height and opacity
  show(element) {
    // Safety guard
    if (!element) return;

    // If a transition is already running, do nothing
    if (this.isLockedTransition(element)) return;

    // Lock transitions so we can't interrupt this animation
    this.lockTransition(element);

    // Force overflow hidden to clip content during animation
    element.style.overflow = "hidden";

    // Mark the element as expanded so CSS can make it visible
    element.setAttribute("data-expanded", "true");

    // Add an opening state which lets CSS start opacity at 0 before transitioning to 1
    element.setAttribute("data-opening", "true");

    // Force a starting height of 0 so we can animate height to content height
    element.style.height = "0px";

    // Wait for the next animation frame so the browser registers
    // the initial styles before we change them
    requestAnimationFrame(() => {
      // Remove the opening flag so opacity can transition to 1
      element.removeAttribute("data-opening");

      // Set the target height, triggering the height transition
      element.style.height = `${element.scrollHeight}px`;
    });

    // Read how long the transition should last
    const duration = this.getTransitionDuration(element);

    // After the transition
    window.setTimeout(() => {
      // Remove the inline height so layout can become flexible again
      element.style.height = "";

      // Remove overflow hidden so shadows and content can overflow again
      element.style.overflow = "";

      // Unlock transitions so future toggles are allowed
      this.unlockTransition(element);
    }, duration);
  }

  // Collapses the element with animated height and opacity
  hide(element) {
    // Safety guard
    if (!element) return;

    // Prevent starting a new animation if one is already running
    if (this.isLockedTransition(element)) return;

    // Lock transitions so we can't interrupt this animation
    this.lockTransition(element);

    // Force overflow hidden to clip content during animation
    element.style.overflow = "hidden";

    // Ensure we are not in an opening state anymore
    element.removeAttribute("data-opening");

    // Freeze the current height so we can animate from
    // this exact value down to 0
    element.style.height = `${element.scrollHeight}px`;

    // Add a closing state so CSS can fade opacity to 0
    element.setAttribute("data-closing", "true");

    // Wait one frame so the frozen height is applied,
    // then trigger the collapse
    requestAnimationFrame(() => {
      // Setting height to 0 triggers the height transition
      element.style.height = "0px";
    });

    // Read how long the transition should last
    const duration = this.getTransitionDuration(element);

    // After the transition
    window.setTimeout(() => {
      // Remove the closing state
      element.removeAttribute("data-closing");

      // Mark the element as no longer expanded
      element.setAttribute("data-expanded", "false");

      // Remove inline height so CSS can control layout again
      element.style.height = "";

      // Keep overflow hidden (or let CSS handle it) since it's closed
      // If you want it completely reset, you can use element.style.overflow = '';
      // but usually hidden is desired when closed.

      // Unlock transitions
      this.unlockTransition(element);
    }, duration);
  }

  // Toggles between show() and hide() depending on current state
  reveal(element) {
    // Read current expanded state from attribute
    const isExpanded = element?.getAttribute("data-expanded") === "true";

    // Collapse if expanded, otherwise expand
    if (isExpanded) this.hide(element);
    else this.show(element);
  }

  handleToggleClick() {
    // Read expanded state from aria-expanded
    const isExpanded = this.toggle.getAttribute("aria-expanded") === "true";

    // If we are about to open, close other items in the same group first
    if (!isExpanded) {
      this.closeOtherGroupItems();
    }

    // Flip aria state first
    this.toggle.setAttribute("aria-expanded", !isExpanded);

    // Toggle reveal animation on the target
    this.reveal(this.target);
  }

  // Grouping methods
  // ----------------------------------------------------------------------

  // Returns the group name for this component, if any
  getGroupName() {
    const name = this.getAttribute("name");
    return name && name.trim() ? name.trim() : null;
  }

  // Returns all other <wc-reveal> instances in the same group
  getGroupItems() {
    const groupName = this.getGroupName();
    if (!groupName) return [];

    // Find all reveals with the same name in the document
    const allReveals = Array.from(
      document.querySelectorAll(
        `${Reveal.tagName}[name="${CSS.escape(groupName)}"]`,
      ),
    );

    // Exclude this instance
    return allReveals.filter((element) => element !== this);
  }

  // Closes all other group items so only one stays open
  closeOtherGroupItems() {
    const items = this.getGroupItems();
    if (!items.length) return;

    items.forEach((item) => {
      // Find the other instance's target the same way it does in setup()
      const itemTargetId = item.getAttribute("target-id");
      const itemTarget = itemTargetId
        ? document.getElementById(itemTargetId)
        : null;

      // If target is missing, skip
      if (!itemTarget) return;

      // If target is already closed, skip
      const isItemExpanded =
        itemTarget.getAttribute("data-expanded") === "true";
      if (!isItemExpanded) return;

      // If item is currently transitioning, skip to avoid fighting its animation
      if (itemTarget.getAttribute("data-transitioning") === "true") return;

      // Update the other button's aria-expanded (if it exists)
      const itemToggle = item.querySelector(Reveal.selectors.toggle);
      if (itemToggle) itemToggle.setAttribute("aria-expanded", "false");

      // Close the other target using the same animation logic
      // We call the instance method so it uses its own helpers if needed.
      item.hide(itemTarget);
    });
  }
}

// Define and register the custom element
window.customElements.define(Reveal.tagName, Reveal);

// Safari 17 cart-drawer footer fix.
// Safari 17 ignores the shadow-DOM slot assignment for the footer, leaving
// the checkout button invisible. For all other browsers the slot works fine.
(function fixCartDrawerFooter() {
  var ua = navigator.userAgent;
  var isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  var safariMatch = ua.match(/Version\/(\d+)\./);
  var isBuggySafari =
    isSafari && safariMatch && parseInt(safariMatch[1], 10) === 17;

  function restoreSlot(cartDrawer) {
    var shadow = cartDrawer.shadowRoot;
    if (!shadow) return;
    var contentPart = shadow.querySelector('[part="content"]');
    if (!contentPart) return;
    if (shadow.querySelector('[part="footer"]')) return;

    var footer = document.createElement("footer");
    footer.setAttribute("part", "footer");
    var slot = document.createElement("slot");
    slot.setAttribute("name", "footer");
    footer.appendChild(slot);
    contentPart.appendChild(footer);

    var form = cartDrawer.querySelector(".cart-drawer__footer");
    if (form && form.parentElement) {
      form.parentElement.setAttribute("slot", "footer");
    }
  }

  function applySpacing(cartDrawer) {
    var el = cartDrawer.querySelector(".cart-drawer-footer-container");
    if (!el) return;
    el.removeAttribute("slot");

    var body =
      cartDrawer.shadowRoot &&
      cartDrawer.shadowRoot.querySelector('[part="body"]');
    if (!body) return;

    el.style.marginTop = "";
    void el.offsetHeight;

    var footerRect = el.getBoundingClientRect();
    var targetTop = window.innerHeight - footerRect.height - 20;
    var gap = targetTop - footerRect.top;

    if (gap > 0) el.style.marginTop = gap + "px";
  }

  function setup(cartDrawer) {
    if (isBuggySafari) {
      var timer;
      function debouncedApply() {
        clearTimeout(timer);
        timer = setTimeout(function () {
          applySpacing(cartDrawer);
        }, 300);
      }

      // All events that can affect the cart drawer layout in Safari 17
      cartDrawer.addEventListener("dialog:before-show", debouncedApply);
      cartDrawer.addEventListener("dialog:after-show", debouncedApply);
      cartDrawer.addEventListener("dialog:before-hide", debouncedApply);
      cartDrawer.addEventListener("dialog:after-hide", debouncedApply);
      cartDrawer.addEventListener("dialog:force-close", debouncedApply);
      cartDrawer.addEventListener("toggle", debouncedApply);
      cartDrawer.addEventListener("cart-drawer:refreshed", debouncedApply);
      cartDrawer.addEventListener("cart:refresh", debouncedApply);
      cartDrawer.addEventListener("cart:change", debouncedApply);
      cartDrawer.addEventListener(
        "cart:prepare-bundled-sections",
        debouncedApply,
      );
      cartDrawer.addEventListener("line-item:change", debouncedApply);
      cartDrawer.addEventListener("line-item:will-change", debouncedApply);

      new ResizeObserver(debouncedApply).observe(cartDrawer);
    } else {
      restoreSlot(cartDrawer);
    }
  }

  function init() {
    var cd = document.querySelector("cart-drawer");
    if (cd) setup(cd);

    // The configurator dispatches these on window (not on the drawer element)
    // and replaces the entire #cart-drawer DOM node after each add-to-cart.
    // We must re-run setup on whatever element is current at that moment.
    [
      "cart:open",
      "theme:cart:open",
      "cart:updated",
      "theme:cart:updated",
      "cart:refresh",
      "theme:cart:refresh",
    ].forEach(function (evName) {
      window.addEventListener(evName, function () {
        var el = document.querySelector("cart-drawer");
        if (el) setup(el);
      });
    });

    // Also watch for the configurator replacing the cart-drawer node in the DOM
    var domObs = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (!node.tagName) return;
          if (node.tagName.toLowerCase() === "cart-drawer") {
            setup(node);
          } else if (node.querySelector) {
            var inner = node.querySelector("cart-drawer");
            if (inner) setup(inner);
          }
        });
      });
    });
    domObs.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();

/**
 * NW Community slider: open modal with full image or Shopify-hosted video.
 */
function initNwCommunitySlider(root) {
  if (!root || root.dataset.nwCommunitySliderInit === "true") return;
  const modal = root.querySelector(".nw-community-slider__modal");
  const modalImg = modal && modal.querySelector(".nw-community-slider__modal-img");
  const modalVideo = modal && modal.querySelector(".nw-community-slider__modal-video");
  const modalNativeVideo = modalVideo && modalVideo.querySelector(".nw-community-slider__modal-native-video");
  const modalPoster = modalVideo && modalVideo.querySelector(".nw-community-slider__modal-poster");
  const modalVolume = modalVideo && modalVideo.querySelector(".nw-community-slider__modal-volume");
  const closeBtn = modal && modal.querySelector(".nw-community-slider__modal-close");
  const prevBtn = modal && modal.querySelector(".nw-community-slider__modal-prev");
  const nextBtn = modal && modal.querySelector(".nw-community-slider__modal-next");
  const mobileMql = window.matchMedia("(max-width: 699px)");

  if (!modal) return;
  root.dataset.nwCommunitySliderInit = "true";

  let slides = [];
  let currentIndex = 0;
  let modalMuted = false;

  function isMobile() {
    return mobileMql.matches;
  }

  function pauseSlideVideos() {
    root.querySelectorAll(".nw-community-slider__slide-video").forEach(function (video) {
      video.pause();
    });
  }

  function playGridVideo(video) {
    if (video.dataset.src && !video.src) {
      video.src = video.dataset.src;
      video.addEventListener("loadeddata", function () {
        video.classList.add("is-ready");
      }, { once: true });
      video.load();
    }
    video.play().catch(function () {});
  }

  function resumeSlideVideos() {
    root.querySelectorAll(".nw-community-slider__slide-video").forEach(playGridVideo);
  }

  function resetModalVideoState() {
    if (modalNativeVideo) {
      modalNativeVideo.classList.remove("is-ready");
      modalNativeVideo.pause();
      modalNativeVideo.controls = false;
      modalNativeVideo.removeAttribute("src");
    }
    if (modalPoster) {
      modalPoster.hidden = true;
      modalPoster.removeAttribute("src");
    }
  }

  function showModalVideoPoster(posterUrl) {
    if (!modalPoster) return;
    if (posterUrl) {
      modalPoster.src = posterUrl;
      modalPoster.hidden = false;
    } else {
      modalPoster.removeAttribute("src");
      modalPoster.hidden = true;
    }
  }

  function markModalVideoReady() {
    if (!modalNativeVideo) return;
    modalNativeVideo.classList.add("is-ready");
  }

  function syncModalVideoUI() {
    if (!modalNativeVideo || modalVideo.hidden) return;
    const mobile = isMobile();
    modalNativeVideo.controls = !mobile;
    if (!modalVolume) return;
    modalVolume.hidden = !mobile;
    const unmuteBtn = modalVolume.querySelector('[data-action="unmute"]');
    const muteBtn = modalVolume.querySelector('[data-action="mute"]');
    if (!unmuteBtn || !muteBtn) return;
    unmuteBtn.hidden = !modalNativeVideo.muted;
    muteBtn.hidden = modalNativeVideo.muted;
  }

  function getSlidesData() {
    return Array.from(root.querySelectorAll(".nw-community-slider__slide-trigger"));
  }

  function showSlide(index) {
    slides = getSlidesData();
    if (!slides.length) return;
    currentIndex = (index + slides.length) % slides.length;
    const trigger = slides[currentIndex];

    if (trigger.dataset.type === "video" && modalNativeVideo) {
      const gridVideo = trigger.querySelector(".nw-community-slider__slide-video");
      const posterImg = trigger.querySelector(".nw-community-slider__slide-poster");
      const media = trigger.querySelector(".nw-community-slider__media--video");
      pauseSlideVideos();
      modalImg.hidden = true;
      modalVideo.hidden = false;
      modalNativeVideo.classList.remove("is-ready");
      if (media && media.dataset.aspectRatio) {
        modalVideo.style.setProperty("--nw-video-aspect-ratio", media.dataset.aspectRatio);
      } else {
        modalVideo.style.removeProperty("--nw-video-aspect-ratio");
      }
      showModalVideoPoster(posterImg && posterImg.src ? posterImg.src : "");
      modalNativeVideo.removeAttribute("poster");
      const videoUrl = gridVideo && (gridVideo.dataset.src || gridVideo.src);
      if (videoUrl) {
        modalNativeVideo.src = videoUrl;
      } else {
        modalNativeVideo.removeAttribute("src");
      }
      modalNativeVideo.muted = modalMuted;
      modalNativeVideo.loop = true;
      modalNativeVideo.currentTime = 0;
      modalNativeVideo.addEventListener("loadeddata", markModalVideoReady, { once: true });
      modalNativeVideo.load();
      syncModalVideoUI();
      modalNativeVideo.play().catch(function () {});
      if (modalNativeVideo.readyState >= 2) {
        markModalVideoReady();
      }
    } else {
      modalVideo.hidden = true;
      modalVideo.style.removeProperty("--nw-video-aspect-ratio");
      if (modalVolume) modalVolume.hidden = true;
      resetModalVideoState();
      modalImg.hidden = false;
      modalImg.src = trigger.getAttribute("data-src");
      modalImg.alt = trigger.getAttribute("data-alt") || "";
    }
  }

  function openModal(index) {
    modalMuted = true;
    showSlide(index);
    modal.hidden = false;
    document.body.style.overflow = "hidden";
  }

  function closeModal() {
    modalMuted = true;
    modal.hidden = true;
    document.body.style.overflow = "";
    modalImg.hidden = true;
    modalImg.removeAttribute("src");
    modalVideo.hidden = true;
    modalVideo.style.removeProperty("--nw-video-aspect-ratio");
    if (modalVolume) modalVolume.hidden = true;
    if (modalNativeVideo) {
      modalNativeVideo.pause();
      modalNativeVideo.controls = false;
    }
    if (modalPoster) {
      modalPoster.hidden = true;
    }
    resumeSlideVideos();
  }

  if (modalVolume) {
    modalVolume.addEventListener("click", function (e) {
      const btn = e.target.closest("[data-action]");
      if (!btn || !modalNativeVideo) return;
      modalNativeVideo.muted = modalMuted = btn.dataset.action === "mute";
    });
  }

  if (modalNativeVideo) {
    modalNativeVideo.addEventListener("volumechange", function () {
      modalMuted = modalNativeVideo.muted;
      syncModalVideoUI();
    });
    modalNativeVideo.addEventListener("click", function () {
      if (!isMobile() || modalVideo.hidden) return;
      if (modalNativeVideo.paused) {
        modalNativeVideo.play().catch(function () {});
      } else {
        modalNativeVideo.pause();
      }
    });
  }

  mobileMql.addEventListener("change", function () {
    if (modal.hidden || modalVideo.hidden) return;
    syncModalVideoUI();
  });

  root.addEventListener("click", function (e) {
    const trigger = e.target.closest(".nw-community-slider__slide-trigger");
    if (!trigger) return;
    e.preventDefault();
    slides = getSlidesData();
    openModal(slides.indexOf(trigger));
  });

  if (closeBtn) closeBtn.addEventListener("click", closeModal);
  if (prevBtn) prevBtn.addEventListener("click", function () { showSlide(currentIndex - 1); });
  if (nextBtn) nextBtn.addEventListener("click", function () { showSlide(currentIndex + 1); });

  document.addEventListener("keydown", function (e) {
    if (modal.hidden) return;
    if (e.key === "Escape") closeModal();
    if (e.key === "ArrowLeft") showSlide(currentIndex - 1);
    if (e.key === "ArrowRight") showSlide(currentIndex + 1);
  });

  const slideContainer = root.querySelector(".slides__container");
  if (slideContainer && "IntersectionObserver" in window) {
    const slideObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        const video = entry.target;
        if (entry.isIntersecting) {
          playGridVideo(video);
        } else {
          video.pause();
        }
      });
    }, { root: slideContainer, threshold: 0.25 });

    slideContainer.querySelectorAll(".nw-community-slider__slide-video").forEach(function (video) {
      slideObserver.observe(video);
    });
  }
}

function initAllNwCommunitySliders() {
  document.querySelectorAll("[data-nw-community-slider]").forEach(initNwCommunitySlider);
}

let _nwCommunitySliderSectionHooked = false;

function bootNwCommunitySliderModule() {
  initAllNwCommunitySliders();
  if (_nwCommunitySliderSectionHooked) return;
  _nwCommunitySliderSectionHooked = true;
  document.addEventListener("shopify:section:load", function (ev) {
    const id = ev.detail && ev.detail.sectionId;
    if (!id) {
      initAllNwCommunitySliders();
      return;
    }
    const sectionEl = document.getElementById("shopify-section-" + id);
    const root = sectionEl && sectionEl.querySelector("[data-nw-community-slider]");
    if (root) initNwCommunitySlider(root);
  });
}

if (typeof customElements !== "undefined" && customElements.whenDefined) {
  customElements.whenDefined("x-modal").then(bootNwCommunitySliderModule);
} else {
  bootNwCommunitySliderModule();
}

document.addEventListener("DOMContentLoaded", function () {
  const btn = document.getElementById("button-open-wizart");
  if (!btn) return;

  const modal = document.getElementById("wizart-modal");
  const backdrop = document.getElementById("wizart-backdrop");
  const closeBtn = document.getElementById("wizart-close");
  const mount = document.getElementById("wizart-visualizer");
  const sku = mount.dataset.productHandle;
  const locale = mount.dataset.locale;

  // Wizart is heavy, so it starts when someone shows interest (hover, touch, focus, click),
  // not on page load. It only loads once; a failed load can be retried.
  let visualizerReady = null;

  function loadVisualizer() {
    if (!visualizerReady) {
      const visualizer = new WizartDeploymentKit.Visualizer({
        token: "ihsq39Nj5FhrWMJCmcbppyy0DxIJkoEQKnq1jHK75MamCfvsdbPEHEasfXSt",
        layoutSettings: {
          targetElement: mount,
          layout: WizartDeploymentKit.LAYOUTS.FILL_PARENT,
        },
        integrationSettings: {
          showBackToWebsiteButton: false,
          locale: locale,
        },
        sceneData: {
          openWithProduct: sku,
        },
      });
      visualizerReady = visualizer.load().then(
        () => visualizer,
        (error) => {
          visualizerReady = null;
          throw error;
        },
      );
    }
    return visualizerReady;
  }

  async function openModal() {
    modal.removeAttribute("inert");
    modal.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
    document.querySelector(".shopify-section--header").style.zIndex = "0";

    // Query at open time since Gorgias loads async
    const chat = document.querySelector("#gorgias-chat-container");
    if (chat) chat.style.zIndex = "0";

    const revisitConsent = document.querySelector(".cky-btn-revisit-wrapper");
    if (revisitConsent) revisitConsent.style.zIndex = "0";

    closeBtn.focus();

    const visualizer = await loadVisualizer();
    // Closed again while Wizart was loading
    if (modal.hasAttribute("inert")) return;
    visualizer.show();

    // Triple Whale tracking
   if (typeof window.TriplePixel  === "function") {
      window.TriplePixel("custom", "wizart_see_in_room_clicked", {
        button_id: "button-open-wizart",
        label: "See wallpaper in your room",
        page: window.location.pathname,
        product_handle: sku,
      });
    }
  }

  function closeModal() {
    modal.setAttribute("inert", "");
    modal.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";
    document.querySelector(".shopify-section--header").style.zIndex = "";

    const chat = document.querySelector("#gorgias-chat-container");
    if (chat) chat.style.zIndex = "";

    const revisitConsent = document.querySelector(".cky-btn-revisit-wrapper");
    if (revisitConsent) revisitConsent.style.zIndex = "";

    btn.focus();
  }

  // Start loading as soon as the pointer or keyboard reaches the button, so it's ready by the click
  btn.addEventListener("pointerenter", loadVisualizer, { once: true });
  btn.addEventListener("focus", loadVisualizer, { once: true });
  btn.addEventListener("click", openModal);
  closeBtn.addEventListener("click", closeModal);
  backdrop.addEventListener("click", closeModal);

  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape") closeModal();
  });
});
/**
 * Web Component: WarrantyUpsell
 * Version: 1.0
 *
 * Handles the warranty upsell UI in the cart drawer.
 * Adds or removes the warranty product based on radio selection.
 */
class WarrantyUpsell extends HTMLElement {
  static tagName = "nw-warranty-upsell";
  static mutationInFlight = false;

  static selectors = {
    radio: 'input[type="radio"]',
    option: ".nw-warranty-upsell__option",
  };

  static async removeWarrantyItems(warrantyItems) {
    if (!Array.isArray(warrantyItems) || warrantyItems.length === 0) return false;

    for (const item of warrantyItems) {
      await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: item.key, quantity: 0 }),
      });
    }

    return true;
  }

  static async removeWarrantyByVariantId(variantId) {
    const cart = await fetch("/cart.js").then((r) => r.json());
    const warrantyItems = cart.items.filter(
      (item) => String(item.variant_id) === String(variantId),
    );
    return WarrantyUpsell.removeWarrantyItems(warrantyItems);
  }

  // Component lifecycle
  // ----------------------------------------------------------------------

  connectedCallback() {
    if (document.readyState !== "loading") {
      requestAnimationFrame(() => this.setup());
    } else {
      document.addEventListener("DOMContentLoaded", () => this.setup(), {
        once: true,
      });
    }
  }

  disconnectedCallback() {
    this.cleanup();
  }

  // Lifecycle hooks
  // ----------------------------------------------------------------------

  setup() {
    this.cleanup();

    // Required data
    this.warrantyVariantId = this.dataset.warrantyVariantId;
    this.warrantyPercentage = this.dataset.warrantyPercentage;

    if (!this.warrantyVariantId) return;

    // Abort controller for scoped listeners
    this.abortController = new AbortController();
    const abortSignal = this.abortController.signal;

    // Bind handlers
    this.handleRadioChange = this.handleRadioChange.bind(this);

    // Attach listeners
    const radios = this.querySelectorAll(WarrantyUpsell.selectors.radio);
    radios.forEach((radio) => {
      radio.addEventListener("change", this.handleRadioChange, {
        signal: abortSignal,
      });
    });
  }

  cleanup() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  // Handlers
  // ----------------------------------------------------------------------

  async handleRadioChange(event) {
    if (WarrantyUpsell.mutationInFlight) return;
    const selected = event.target.value;

    WarrantyUpsell.mutationInFlight = true;
    this.setLoading(true);

    try {
      if (selected === "protect") {
        await this.addWarranty();
      } else {
        await this.removeWarranty();
      }

      // Keep the component locked until cart drawer finishes re-rendering.
      await this.waitForCartDrawerRefresh();
    } finally {
      this.setLoading(false);
      WarrantyUpsell.mutationInFlight = false;
    }
  }

  // Cart methods
  // ----------------------------------------------------------------------

  async addWarranty() {
    try {
      const cart = await fetch("/cart.js").then((r) => r.json());
      const hasWarranty = cart.items.some(
        (item) => String(item.variant_id) === String(this.warrantyVariantId),
      );

      if (hasWarranty) {
        return;
      }

      await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [
            {
              id: Number(this.warrantyVariantId),
              quantity: 1,
              properties: {
                _warranty_percentage: this.warrantyPercentage,
              },
            },
          ],
        }),
      });

      this.refreshCart();
    } catch (error) {
      console.error("WarrantyUpsell: failed to add warranty", error);
    }
  }

  async removeWarranty() {
    try {
      const removed = await WarrantyUpsell.removeWarrantyByVariantId(
        this.warrantyVariantId,
      );
      if (removed) this.refreshCart();
    } catch (error) {
      console.error("WarrantyUpsell: failed to remove warranty", error);
    }
  }

  // Helpers
  // ----------------------------------------------------------------------

  /**
   * Dispatches the Prestige cart refresh event.
   * This causes the cart drawer to re-render with updated contents.
   */
  refreshCart() {
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:refresh", { bubbles: true }),
    );
  }

  waitForCartDrawerRefresh(timeout = 1500) {
    return new Promise((resolve) => {
      let done = false;

      const finish = () => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        resolve();
      };

      const onRefreshed = () => finish();

      document.addEventListener("cart-drawer:refreshed", onRefreshed, {
        once: true,
      });

      const timer = window.setTimeout(finish, timeout);
    });
  }

  /**
   * Disables/enables radio inputs during async cart operations
   * to prevent duplicate requests.
   */
  setLoading(isLoading) {
    const radios = this.querySelectorAll(WarrantyUpsell.selectors.radio);
    radios.forEach((radio) => {
      radio.disabled = isLoading;
    });
    this.toggleAttribute("loading", isLoading);
  }
}

// Define and register the custom element
window.customElements.define(WarrantyUpsell.tagName, WarrantyUpsell);

/**
 * Remove orphan warranty rows when no main product remains.
 */
(function initWarrantyOrphanGuard() {
  const warrantyVariantId = document.querySelector("cart-drawer")?.dataset
    ?.warrantyVariantId;
  if (!warrantyVariantId) return;

  let inFlight = false;
  const isSample = (item) =>
    item?.variant_title?.includes("Sample") || item?.title?.includes("Sample");

  document.addEventListener("cart:change", async (event) => {
    if (inFlight) return;

    const cart = event.detail?.cart;
    if (!cart?.items?.length) return;

    const warrantyItems = cart.items.filter(
      (item) => String(item.variant_id) === String(warrantyVariantId),
    );
    if (warrantyItems.length === 0) return;

    const hasMainProduct = cart.items.some(
      (item) =>
        String(item.variant_id) !== String(warrantyVariantId) && !isSample(item),
    );
    if (hasMainProduct) return;

    inFlight = true;
    try {
      const removed = await WarrantyUpsell.removeWarrantyItems(warrantyItems);
      if (!removed) return;

      document.documentElement.dispatchEvent(
        new CustomEvent("cart:refresh", { bubbles: true }),
      );
    } catch (error) {
      console.error("WarrantyOrphanGuard: failed to remove orphan warranty", error);
    } finally {
      inFlight = false;
    }
  });
})();

/**
 * Below the theme md breakpoint, toolbar filter popovers are reparented to document.body.
 * Inputs stay linked via form="FacetToolbarForm-…" but change no longer bubbles to facets-form,
 * so we re-dispatch change so update-on-change / requestSubmit still run.
 */
document.addEventListener("change", (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  const form = target.form;
  if (!form?.id?.startsWith("FacetToolbarForm-")) return;
  if (form.contains(target)) return;
  const facetsForm = form.closest("facets-form");
  facetsForm?.dispatchEvent(new Event("change", { bubbles: true }));
});

/** Fit collection teaser to N lines; Liquid cannot know line breaks. */
class NwCollectionDescription extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector("[data-nw-clamp-content]");
    this.textEl = this.querySelector("[data-nw-clamp-text]");
    this.trigger = this.querySelector("[data-nw-clamp-trigger]");
    if (!this.content || !this.textEl || !this.trigger) return;

    this.fullText = this.textEl.textContent.replace(/\s+/g, " ").trim();
    this.words = this.fullText.split(" ").filter(Boolean);
    this.lastWidth = -1;
    this.lastLines = -1;
    this.fit = this.fit.bind(this);

    this.fit();
    this.resizeObserver = new ResizeObserver(this.fit);
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  fit() {
    const width = Math.round(this.getBoundingClientRect().width);
    const lines = Number.parseFloat(
      getComputedStyle(this).getPropertyValue("--nw-desc-lines"),
    );
    if (
      width === this.lastWidth &&
      lines === this.lastLines &&
      this.classList.contains("is-ready")
    ) {
      return;
    }
    this.lastWidth = width;
    this.lastLines = lines;

    this.classList.remove("is-ready");
    this.trigger.hidden = true;
    this.textEl.textContent = this.fullText;

    const maxHeight = this.maxHeight();
    if (maxHeight == null || this.content.scrollHeight <= maxHeight + 1) {
      this.classList.add("is-ready");
      return;
    }

    this.trigger.hidden = false;

    let lo = 0;
    let hi = this.words.length;
    let best = 1;

    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2);
      this.textEl.textContent =
        (mid > 0 ? this.words.slice(0, mid).join(" ") : "") + "…";

      if (this.content.scrollHeight <= maxHeight + 1) {
        best = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }

    this.textEl.textContent =
      this.words.slice(0, Math.max(1, best)).join(" ") + "…";
    this.classList.add("is-ready");
  }

  maxHeight() {
    const lines = Number.parseFloat(
      getComputedStyle(this).getPropertyValue("--nw-desc-lines"),
    );
    const lineHeight = Number.parseFloat(
      getComputedStyle(this.content).lineHeight,
    );
    if (
      !Number.isFinite(lines) ||
      !Number.isFinite(lineHeight) ||
      lineHeight <= 0
    ) {
      return null;
    }
    return lineHeight * lines;
  }
}

if (!window.customElements.get("nw-collection-description")) {
  window.customElements.define(
    "nw-collection-description",
    NwCollectionDescription,
  );
}

